import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Radio, Tag} from 'antd';
import classNames from 'classnames';
import _ from 'lodash';

import {CheckOutlined, CloseOutlined} from '@/icons';
import DepartUserTagSelector from '@/page/admin/selector/DepartUserTagSelector';
import {DepartTag, AppTagTag, TagTag, UserTag} from '@/component';
import {convertTos, isEmptySelData} from './utils';
import {getMessageViewerCount} from '@/api/message';
import UserExtLabel from '@/page/component/UserExtLabel';

import Item from './Item';

import styles from './SendArea.module.less';
import {useAppStore} from '@/page/app/context';

const SendArea = forwardRef(({appId}, ref) => {
    const selectorRef = useRef();
    const [toAll, setToAll] = useState(true);

    // 格式为 {departs: [{id, name}], users: [{id, name}], tags: [{id, name}]}
    const [selData, setSelData] = useState({});
    const [viewerCount, setViewerCount] = useState(0);

    const [appStore] = useAppStore();
    const app = _.get(appStore, 'app', {});
    const allowDepartIds = _.get(app, 'allowDeparts', []).map(({allowId}) => allowId);
    const allowUsers = _.get(app, 'allowUsers', []);
    // console.log('SendArea', appStore, allowUsers, allowDepartIds);

    useEffect(() => {
        const tos = convertTos(selData);
        getMessageViewerCount(appId, toAll, tos).then(({data}) => {
            // console.log(data);
            setViewerCount(data ?? 0);
        });
    }, [appId, selData, toAll]);

    useImperativeHandle(ref, () => ({
        getData: () => {
            if (toAll) {
                return {toAll, tos: {}, toUserCount: viewerCount};
            } else {
                return {toAll, tos: selData, toUserCount: viewerCount};
            }
        },
        clear: () => {
            setSelData({});
            setToAll(true);
        },
    }));

    function onRadioChange(e) {
        setToAll(e.target.value);
    }

    function onSelectClick() {
        selectorRef.current.showModal({initialValues: selData});
    }

    function onSelectClearClick() {
        setSelData({});
    }

    function onSelect(values) {
        // console.log('onSelect', values);
        setSelData(values);
        return true;
    }

    function onTagClose(type, closeId) {
        return () => {
            const items = selData[type];
            const newItems = items.filter(({id}) => id !== closeId);
            setSelData({...selData, [type]: newItems});
        };
    }

    function renderSelItems(title, type, Comp, items) {
        if (_.isEmpty(items)) {
            return null;
        }

        return (
            <Item
                title={title}
                className={styles.selectedItems}
                titleStyle={{minWidth: 80, textAlign: 'right'}}
            >
                {items.map(({id, name}) => (
                    <Comp key={id} closable onClose={onTagClose(type, id)}>
                        {name}
                        <UserExtLabel userId={id} />
                    </Comp>
                ))}
            </Item>
        );
    }

    const {departs, users, tags, apptags} = selData || {};

    return (
        <Item title="发送范围" className={styles.container}>
            <div className={styles.selLine}>
                <div style={{flex: 1}}>
                    <Radio.Group value={toAll} onChange={onRadioChange}>
                        <Radio value={true}>应用可见范围内所有人</Radio>
                        <Radio value={false}>范围发送</Radio>
                    </Radio.Group>

                    <Button
                        type="link"
                        icon={<CheckOutlined />}
                        disabled={toAll}
                        className={styles.selectedBtn}
                        onClick={onSelectClick}
                    >
                        选择发送范围
                    </Button>

                    {!toAll && !isEmptySelData(selData) && (
                        <Button
                            type="text"
                            style={{color: '#8c8c8c'}}
                            icon={<CloseOutlined />}
                            className={styles.selectedBtn}
                            onClick={onSelectClearClick}
                        >
                            清除所有已选
                        </Button>
                    )}
                </div>
                <div>
                    <Tag>
                        共 <strong>{viewerCount}</strong> 人
                    </Tag>
                </div>
            </div>
            {!isEmptySelData(selData) && (
                <div
                    className={classNames({[styles.hidden]: toAll, [styles.selectedArea]: !toAll})}
                >
                    {renderSelItems('部门', 'departs', DepartTag, departs)}
                    {renderSelItems('成员', 'users', UserTag, users)}
                    {renderSelItems('标签', 'tags', TagTag, tags)}
                    {/*{renderSelItems('AD 应用标签', 'apptags', AppTagTag, apptags)}*/}
                </div>
            )}

            <DepartUserTagSelector
                ref={selectorRef}
                modalTitle="选择发送范围"
                allowDepartIds={allowDepartIds}
                allowUsers={allowUsers}
                onSelect={onSelect}
            />
        </Item>
    );
});

SendArea.displayName = 'SendArea';

SendArea.propTypes = {
    appId: PropTypes.string.isRequired,
};

export default SendArea;
